import { clearSessionToken, getSessionToken, setSessionToken } from "../auth/session";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const DEV_MODE = process.env.REACT_APP_DEV_MODE === "true";

const isDemoMode = () => {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem("demoMode") === "1";
};

const getAuthHeaders = async () => {
  const headers = { "Content-Type": "application/json" };
  if (DEV_MODE || isDemoMode()) {
    const demo = isDemoMode();
    headers["x-dev-sub"] = demo ? "demo-user" : process.env.REACT_APP_DEV_SUB || "dev-user";
    headers["x-dev-email"] = demo ? "demo@reflecta.app" : process.env.REACT_APP_DEV_EMAIL || "dev@reflecta.app";
    headers["x-dev-admin"] = demo ? "false" : process.env.REACT_APP_DEV_ADMIN || "false";
    return headers;
  }

  try {
    const token = getSessionToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  } catch {
    // Not authenticated
  }
  return headers;
};

export const loginWithPassword = async ({ email, password }) => {
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const data = await handleResponse(response);
  setSessionToken(data.token);
  return data.user;
};

export const registerWithPassword = async ({ email, password }) => {
  const response = await fetch(`${API_BASE_URL}/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const data = await handleResponse(response);
  setSessionToken(data.token);
  return data.user;
};

export const logoutSession = async () => {
  try {
    await authFetch(`${API_BASE_URL}/auth/logout`, { method: "POST" });
  } finally {
    clearSessionToken();
  }
};

export const changePassword = async ({ oldPassword, newPassword }) => {
  const response = await authFetch(`${API_BASE_URL}/auth/change-password`, {
    method: "POST",
    body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
  });
  return handleResponse(response);
};

const authFetch = async (url, options = {}) => {
  const headers = { ...(await getAuthHeaders()), ...options.headers };
  return fetch(url, { ...options, headers });
};

const handleResponse = async (response) => {
  if (!response.ok) {
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      const errorData = await response.json();
      throw new Error(
        `HTTP error! status: ${response.status} - ${
          errorData.detail || JSON.stringify(errorData)
        }`
      );
    } else {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }
  }
  return response.json();
};

const normalizeEntry = (entry) => ({
  ...entry,
  id: entry.id || entry.entry_id,
  date: entry.date || entry.entry_date || (entry.created_at || "").slice(0, 10),
  activities: Array.isArray(entry.activities)
    ? entry.activities
    : entry.activities
    ? String(entry.activities)
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    : [],
  sentiments: Array.isArray(entry.sentiments)
    ? entry.sentiments
    : entry.sentiments
    ? String(entry.sentiments)
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    : [],
});

const normalizeGoal = (goal) => ({
  ...goal,
  id: goal.id || goal.goal_id,
  targetDate: goal.targetDate || goal.target_date || null,
});

const unwrapEntries = (payload) => {
  const entries = Array.isArray(payload) ? payload : payload?.entries || [];
  return entries.map(normalizeEntry);
};

const unwrapGoals = (payload) => {
  const goals = Array.isArray(payload) ? payload : payload?.goals || [];
  return goals.map(normalizeGoal);
};

export const fetchMe = async () => {
  let response = await authFetch(`${API_BASE_URL}/me`);
  if (response.status === 404) {
    response = await authFetch(`${API_BASE_URL}/auth/me`);
  }
  const data = await handleResponse(response);
  return {
    ...data,
    subscription_tier: data.subscription_tier || "free",
    subscription_status: data.subscription_status || "free",
    is_demo: isDemoMode(),
  };
};

export const updateProfile = async (profileData) => {
  const response = await authFetch(`${API_BASE_URL}/me`, {
    method: "PATCH",
    body: JSON.stringify(profileData),
  });
  return handleResponse(response);
};

export const updateSubscription = async ({ tier }) => {
  const response = await authFetch(`${API_BASE_URL}/me/subscription`, {
    method: "POST",
    body: JSON.stringify({ tier }),
  });
  return handleResponse(response);
};

export const fetchCalendarData = async () => {
  const [entriesResponse, goalsResponse] = await Promise.all([
    authFetch(`${API_BASE_URL}/journal/entries/`),
    authFetch(`${API_BASE_URL}/goals/`),
  ]);

  const entriesData = await handleResponse(entriesResponse);
  const goalsData = await handleResponse(goalsResponse);

  return {
    journalEntries: unwrapEntries(entriesData),
    goals: unwrapGoals(goalsData),
  };
};

const calendarUpdateListeners = [];

export const addCalendarUpdateListener = (listener) => {
  calendarUpdateListeners.push(listener);
};

export const removeCalendarUpdateListener = (listener) => {
  const index = calendarUpdateListeners.indexOf(listener);
  if (index !== -1) {
    calendarUpdateListeners.splice(index, 1);
  }
};

const notifyCalendarUpdate = () => {
  calendarUpdateListeners.forEach((listener) => listener());
};

export const fetchJournalEntries = async () => {
  const response = await authFetch(`${API_BASE_URL}/journal/entries/`);
  const data = unwrapEntries(await handleResponse(response));
  const sortedEntries = data.sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );
  return sortedEntries;
};

export const createJournalEntry = async (entryData) => {
  const response = await authFetch(`${API_BASE_URL}/journal/entries/`, {
    method: "POST",
    body: JSON.stringify(entryData),
  });
  const result = normalizeEntry(await handleResponse(response));
  notifyCalendarUpdate();
  return result;
};

export const updateJournalEntry = async (entryId, entryData) => {
  const response = await authFetch(`${API_BASE_URL}/journal/entries/${entryId}`, {
    method: "PUT",
    body: JSON.stringify(entryData),
  });
  const result = normalizeEntry(await handleResponse(response));
  notifyCalendarUpdate();
  return result;
};

export const deleteJournalEntry = async (entryId) => {
  const response = await authFetch(`${API_BASE_URL}/journal/entries/${entryId}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      const errorData = await response.json();
      throw new Error(
        `HTTP error! status: ${response.status} - ${
          errorData.detail || JSON.stringify(errorData)
        }`
      );
    } else {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }
  }
  notifyCalendarUpdate();
  return true;
};

export const fetchGoals = async () => {
  const response = await authFetch(`${API_BASE_URL}/goals/`);
  return unwrapGoals(await handleResponse(response));
};

export const getAnalyticsSummary = async (period) => {
  const response = await authFetch(
    `${API_BASE_URL}/analytics/summary/?period=${period}`
  );
  return handleResponse(response);
};

export const getAnalyticsTrends = async (period) => {
  const response = await authFetch(
    `${API_BASE_URL}/analytics/trends/?period=${period}`
  );
  return handleResponse(response);
};

export const getAnalyticsCorrelations = async (period) => {
  const response = await authFetch(
    `${API_BASE_URL}/analytics/correlations/?period=${period}`
  );
  return handleResponse(response);
};

export const getAnalyticsStats = async (period) => {
  const response = await authFetch(
    `${API_BASE_URL}/analytics/stats/?period=${period}`
  );
  return handleResponse(response);
};

export const enhanceGoalDescription = async (title, description) => {
  let response = await authFetch(`${API_BASE_URL}/goals/enhance-description`, {
    method: "POST",
    body: JSON.stringify({ title, description }),
  });
  if (response.status === 404 || response.status === 405) {
    response = await authFetch(`${API_BASE_URL}/goals/enhance`, {
      method: "POST",
      body: JSON.stringify({ title, description }),
    });
  }
  const data = await handleResponse(response);
  return typeof data === "string" ? data : data.enhanced_description;
};

export const requestDataExport = async () => {
  const response = await authFetch(`${API_BASE_URL}/me/data-export`, {
    method: "POST",
  });
  if (!response.ok) {
    return handleResponse(response);
  }
  // 200 = sync export (local dev): download the JSON file directly
  if (response.status === 200) {
    const blob = await response.blob();
    const disposition = response.headers.get("content-disposition") || "";
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match ? match[1] : "reflecta-export.json";
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    return { status: "downloaded", filename };
  }
  return handleResponse(response);
};

export const requestAccountDeletion = async () => {
  const response = await authFetch(`${API_BASE_URL}/me/data-deletion`, {
    method: "POST",
  });
  return handleResponse(response);
};

export const createGoal = async (goalData) => {
  const response = await authFetch(`${API_BASE_URL}/goals/`, {
    method: "POST",
    body: JSON.stringify(goalData),
  });
  const result = normalizeGoal(await handleResponse(response));
  notifyCalendarUpdate();
  return result;
};

export const updateGoal = async (goalId, goalData) => {
  const response = await authFetch(`${API_BASE_URL}/goals/${goalId}`, {
    method: "PUT",
    body: JSON.stringify(goalData),
  });
  const result = normalizeGoal(await handleResponse(response));
  notifyCalendarUpdate();
  return result;
};

export const deleteGoal = async (goalId) => {
  const response = await authFetch(`${API_BASE_URL}/goals/${goalId}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      const errorData = await response.json();
      throw new Error(
        `HTTP error! status: ${response.status} - ${
          errorData.detail || JSON.stringify(errorData)
        }`
      );
    } else {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }
  }
  notifyCalendarUpdate();
  return true;
};

let activeConversationId = null;

export const createConversation = async (title = null) => {
  const response = await authFetch(`${API_BASE_URL}/ai/conversations`, {
    method: "POST",
    body: JSON.stringify({ title }),
  });
  return handleResponse(response);
};

export const sendConversationMessage = async (conversationId, content) => {
  const response = await authFetch(
    `${API_BASE_URL}/ai/conversations/${conversationId}/messages`,
    {
      method: "POST",
      body: JSON.stringify({ content }),
    }
  );
  return handleResponse(response);
};

export const sendChatMessage = async (message) => {
  try {
    if (!activeConversationId) {
      const conversation = await createConversation("Journal Assistant");
      activeConversationId = conversation.conversation_id;
    }
    const data = await sendConversationMessage(activeConversationId, message);
    return data.content;
  } catch (error) {
    if (!error.message.includes("status: 404")) {
      throw error;
    }
    const response = await authFetch(`${API_BASE_URL}/ai/chat/`, {
      method: "POST",
      body: JSON.stringify({ message }),
    });
    const data = await handleResponse(response);
    return data.text;
  }
};

export const getJournalQuestion = async (content, entryId = null) => {
  const response = await authFetch(`${API_BASE_URL}/ai/journal-question/`, {
    method: "POST",
    body: JSON.stringify({ content, entry_id: entryId }),
  });
  return handleResponse(response);
};

export const recommendGoals = async () => {
  const response = await authFetch(`${API_BASE_URL}/goals/recommend`, {
    method: "POST",
  });
  return handleResponse(response);
};

export const getAdminStats = async () => {
  const response = await authFetch(`${API_BASE_URL}/admin/stats`);
  if (response.status === 403) {
    throw new Error("Forbidden – admin access required (HTTP 403)");
  }
  return handleResponse(response);
};

export const getAdminUsers = async ({ search, filterStatus, page = 1 } = {}) => {
  const params = new URLSearchParams({ page });
  if (search) params.set("search", search);
  if (filterStatus) params.set("filter_status", filterStatus);
  const response = await authFetch(`${API_BASE_URL}/admin/users?${params}`);
  if (response.status === 403) throw new Error("Admin access required (HTTP 403)");
  const data = await handleResponse(response);
  const users = (data.users || []).map((u) => ({ ...u, id: u.id || u.sub }));
  return { ...data, users };
};

export const patchAdminUser = async (userId, patch) => {
  const response = await authFetch(`${API_BASE_URL}/admin/users/${userId}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  if (response.status === 403) throw new Error("Admin access required (HTTP 403)");
  return handleResponse(response);
};

export const deleteAdminUser = async (userId, confirmEmail) => {
  const response = await authFetch(`${API_BASE_URL}/admin/users/${userId}`, {
    method: "DELETE",
    body: JSON.stringify({ email: confirmEmail }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`HTTP ${response.status} – ${text}`);
  }
  return true;
};

// --- DSGVO-Tooling (Ticket #627 / Backend #653) ---

export const triggerDsgvoExport = async ({ target_email, justification }) => {
  const response = await authFetch(`${API_BASE_URL}/admin/dsgvo/export`, {
    method: "POST",
    body: JSON.stringify({ target_email, justification }),
  });
  if (response.status === 403) throw new Error("Admin access required (HTTP 403)");
  return handleResponse(response);
};

export const triggerDsgvoDelete = async ({
  target_email,
  mode,
  justification,
}) => {
  const response = await authFetch(`${API_BASE_URL}/admin/dsgvo/delete`, {
    method: "POST",
    body: JSON.stringify({ target_email, mode, justification }),
  });
  if (response.status === 403) throw new Error("Admin access required (HTTP 403)");
  return handleResponse(response);
};

export const listDsgvoExportJobs = async () => {
  const response = await authFetch(`${API_BASE_URL}/admin/dsgvo/exports`);
  if (response.status === 403) throw new Error("Admin access required (HTTP 403)");
  return handleResponse(response);
};

export const listAdminAuditLog = async ({
  admin,
  target,
  action,
  page = 1,
  limit = 10,
} = {}) => {
  const params = new URLSearchParams({ page, limit });
  if (admin) params.set("admin_user_id", admin);
  if (target) params.set("target_user_id", target);
  if (action) params.set("action", action);
  const response = await authFetch(
    `${API_BASE_URL}/admin/audit-log?${params}`
  );
  if (response.status === 403) throw new Error("Admin access required (HTTP 403)");
  return handleResponse(response);
};

// --- Admin Subscription Management (Ticket #637) ---

export const getAdminSubscriptions = async ({ plan, status, search, inactiveDays, limit = 50, cursor } = {}) => {
  const params = new URLSearchParams({ limit });
  if (plan) params.set("plan", plan);
  if (status) params.set("status", status);
  if (search) params.set("search", search);
  if (inactiveDays) params.set("inactive_days", inactiveDays);
  if (cursor) params.set("cursor", cursor);
  const response = await authFetch(`${API_BASE_URL}/admin/subscriptions?${params}`);
  if (response.status === 403) throw new Error("Admin access required (HTTP 403)");
  const data = await handleResponse(response);
  return {
    ...data,
    next_cursor: data.next_cursor || data.cursor || null,
    subscriptions: (data.subscriptions || []).map((subscription) => ({
      ...subscription,
      user_id: subscription.user_id || subscription.sub,
      tier: subscription.tier || subscription.subscription_tier || "free",
      period_end: subscription.period_end || subscription.current_period_end || null,
    })),
  };
};

export const getAdminSubscriptionDetail = async (userId) => {
  const response = await authFetch(`${API_BASE_URL}/admin/subscriptions/${userId}`);
  if (response.status === 403) throw new Error("Admin access required (HTTP 403)");
  return handleResponse(response);
};

export const patchAdminSubscription = async (userId, patch) => {
  const response = await authFetch(`${API_BASE_URL}/admin/subscriptions/${userId}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  if (response.status === 403) throw new Error("Admin access required (HTTP 403)");
  return handleResponse(response);
};

export const refundAdminSubscription = async (userId, body) => {
  const response = await authFetch(`${API_BASE_URL}/admin/subscriptions/${userId}/refund`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (response.status === 403) throw new Error("Admin access required (HTTP 403)");
  return handleResponse(response);
};

export const cancelAdminSubscription = async (userId, body) => {
  const response = await authFetch(`${API_BASE_URL}/admin/subscriptions/${userId}/cancel`, {
    method: "POST",
    body: JSON.stringify({ action: "cancel", ...body }),
  });
  if (response.status === 403) throw new Error("Admin access required (HTTP 403)");
  return handleResponse(response);
};

// --- Business Metrics (Ticket #636) ---

export const getAdminStatsSubscriptions = async (period = "30d") => {
  const response = await authFetch(
    `${API_BASE_URL}/admin/stats/subscriptions?period=${period}`
  );
  if (response.status === 403) throw new Error("Admin access required (HTTP 403)");
  return handleResponse(response);
};

export const getAdminStatsConversion = async (period = "30d") => {
  const response = await authFetch(
    `${API_BASE_URL}/admin/stats/conversion?period=${period}`
  );
  if (response.status === 403) throw new Error("Admin access required (HTTP 403)");
  return handleResponse(response);
};

export const getAdminStatsProfit = async (period = "30d") => {
  const response = await authFetch(
    `${API_BASE_URL}/admin/stats/profit?period=${period}`
  );
  if (response.status === 403) throw new Error("Admin access required (HTTP 403)");
  return handleResponse(response);
};

export const getAdminPricing = async () => {
  const response = await authFetch(`${API_BASE_URL}/admin/pricing`);
  if (response.status === 403) throw new Error("Admin access required (HTTP 403)");
  return handleResponse(response);
};

export const captureLeadEmail = async ({ email, marketing_consent }) => {
  const response = await fetch(`${API_BASE_URL}/leads`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, marketing_consent }),
  });
  // 200 = already captured, 201 = new lead — both are fine
  if (response.status === 201 || response.status === 200) return;
  throw new Error("lead capture failed");
};

export const getAdminLeads = async () => {
  const response = await authFetch(`${API_BASE_URL}/admin/leads`);
  if (response.status === 403) throw new Error("Admin access required (HTTP 403)");
  return handleResponse(response);
};
